import React from 'react';
import { useProposalAIGenerator } from '../hooks';
import { ProposalSegmentsController } from 'modules/proposals/hooks/useProposalSegments';
import { ProposalAIGeneratorBriefSection } from './ProposalAIGeneratorBriefSection';
import { ProposalAIGeneratorCollapsibleSection } from './ProposalAIGeneratorCollapsibleSection';
import { ProposalAIGeneratorPreviewSection } from './ProposalAIGeneratorPreviewSection';
import { ProposalAIGeneratorPromptSection } from './ProposalAIGeneratorPromptSection';
import { useIntl } from 'react-intl';

interface Props {
  proposalSegments: ProposalSegmentsController;
}

export const ProposalAIGenerator: React.FC<Props> = ({ proposalSegments }) => {
  const { formatMessage } = useIntl();
  const aiGenerator = useProposalAIGenerator(proposalSegments);

  if (!aiGenerator.shouldRender) {
    return null;
  }

  return (
    <section className="proposal-ai-generator">
      <div className="proposal-ai-generator__header">
        <p className="t-label m-bottom-0">
          {formatMessage({ id: 'proposal-ai.eyebrow' })}
        </p>
        <h2 className="t-gamma m-top--tny m-bottom--tny">
          {formatMessage({ id: 'proposal-ai.title' })}
        </h2>
        <p className="t-text-2 m-bottom-0">
          {formatMessage({ id: 'proposal-ai.description' })}
        </p>
      </div>

      <div className="proposal-ai-generator__steps">
        <ProposalAIGeneratorCollapsibleSection
          id="proposal-ai-prompt-section"
          stepNumber={1}
          title={formatMessage({ id: 'proposal-ai.prompt.section-title' })}
          summary={aiGenerator.prompt}
          isExpanded={aiGenerator.expandedSections.includes('prompt')}
          isComplete={aiGenerator.hasBrief}
          isLocked={false}
          onToggle={() => aiGenerator.toggleSection('prompt')}
        >
          <ProposalAIGeneratorPromptSection
            prompt={aiGenerator.prompt}
            hasBrief={aiGenerator.hasBrief}
            hasGenerated={!!aiGenerator.generated}
            canExpandBrief={aiGenerator.canExpandBrief}
            isExpandingBrief={aiGenerator.isExpandingBrief}
            onPromptChange={aiGenerator.setPrompt}
            onExpandBrief={aiGenerator.handleExpandBrief}
            onReset={aiGenerator.handleReset}
          />
        </ProposalAIGeneratorCollapsibleSection>

        <ProposalAIGeneratorCollapsibleSection
          id="proposal-ai-brief-section"
          stepNumber={2}
          title={formatMessage({ id: 'proposal-ai.brief.title' })}
          summary={aiGenerator.briefDraft?.proposalNameSuggestion}
          isExpanded={aiGenerator.expandedSections.includes('brief')}
          isComplete={!!aiGenerator.generated}
          isLocked={!aiGenerator.hasBrief}
          onToggle={() => aiGenerator.toggleSection('brief')}
        >
          {aiGenerator.briefDraft && (
            <ProposalAIGeneratorBriefSection
              briefDraft={aiGenerator.briefDraft}
              onBriefDraftChange={aiGenerator.updateBriefDraft}
              onGenerateBlocks={aiGenerator.handleGenerateBlocks}
              isGeneratingBlocks={aiGenerator.isGeneratingBlocks}
            />
          )}
        </ProposalAIGeneratorCollapsibleSection>

        <ProposalAIGeneratorCollapsibleSection
          id="proposal-ai-preview-section"
          stepNumber={3}
          isLastStep
          title={formatMessage({ id: 'proposal-ai.preview.title' })}
          summary={
            aiGenerator.generated
              ? aiGenerator.generatedProposalNameSuggestion
              : undefined
          }
          isExpanded={aiGenerator.expandedSections.includes('preview')}
          isComplete={false}
          isLocked={!aiGenerator.generated}
          onToggle={() => aiGenerator.toggleSection('preview')}
        >
          {aiGenerator.generated && (
            <ProposalAIGeneratorPreviewSection
              generated={aiGenerator.generated}
              currentProposalName={aiGenerator.currentProposalName}
              generatedProposalNameSuggestion={
                aiGenerator.generatedProposalNameSuggestion
              }
              currentSegmentsLength={aiGenerator.currentSegmentsLength}
              isGeneratedStale={aiGenerator.isGeneratedStale}
              onApplyGenerated={aiGenerator.handleApplyGenerated}
              onServicePreviewSelectionChange={
                aiGenerator.handleServicePreviewSelectionChange
              }
              servicePreviewStates={aiGenerator.servicePreviewStates}
              onUseSuggestedName={aiGenerator.handleUseSuggestedName}
            />
          )}
        </ProposalAIGeneratorCollapsibleSection>
      </div>
    </section>
  );
};
